import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, Phone, Mail, Loader2, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { THC } from "@/lib/tankHealthCheck";
import { CampaignHeader } from "@/components/tank-health-check/CampaignHeader";
import { CampaignFooter } from "@/components/tank-health-check/CampaignFooter";

/** Jun 2026 — out-of-region referral confirmation (never an appointment). */
export default function TankWaterHealthCheckReferralReceived() {
  const { search } = useLocation();
  const reference = new URLSearchParams(search).get("ref") || "";
  useSEO({ title: "Enquiry received | AWARE Water Testing", noindex: true });
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!reference) { setLoading(false); return; }
    api.get(`/tank-water-health-check/referrals/${encodeURIComponent(reference)}`)
      .then(({ data }) => setReferral(data.referral))
      .catch(() => setReferral(null))
      .finally(() => setLoading(false));
  }, [reference]);

  const partner = referral?.partner;

  return (
    <div style={{ backgroundColor: "#FFFFFF", color: THC.body }} data-testid="thc-referral-received-page">
      <CampaignHeader />
      <main className="mx-auto max-w-[900px] px-[18px] md:px-7 py-10 md:py-14">
        {loading ? (
          <div className="flex items-center gap-2 text-[15px]" style={{ color: THC.muted }}>
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Loading your enquiry…
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-9 h-9 shrink-0" style={{ color: THC.green }} aria-hidden="true" />
              <h1 className="font-black uppercase leading-[1.05] text-4xl sm:text-5xl"
                style={{ color: THC.navy, fontFamily: "'Outfit',system-ui,sans-serif" }}>
                Thanks — we&rsquo;ve received your enquiry
              </h1>
            </div>
            <p className="mt-5 text-base md:text-lg leading-[1.55]" data-testid="thc-referral-received-message">
              Thanks — we&rsquo;ve received your Tank Water Health Check enquiry.
              Your property is outside our direct Sunshine Coast service area, so
              we&rsquo;ve referred your enquiry to a local Pristine Water Systems Partner
              who services your region. They may contact you directly to discuss your
              water system and suitable next steps.
              {partner ? " You can also contact them directly using the details below." : ""}
            </p>

            {referral?.property_address && (
              <div className="mt-5 inline-flex items-start gap-2 rounded-[10px] border px-3 py-2"
                style={{ borderColor: THC.border, backgroundColor: THC.faint }}
                data-testid="thc-referral-received-address">
                <MapPin className="w-4 h-4 mt-0.5" style={{ color: THC.muted }} aria-hidden="true" />
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: THC.muted }}>
                    PROPERTY
                  </div>
                  <div className="text-[15px] font-bold" style={{ color: THC.navy }}>
                    {referral.property_address}
                  </div>
                </div>
              </div>
            )}

            {partner ? (
              <div className="mt-7 rounded-[16px] border-2 p-5 md:p-6"
                style={{ borderColor: THC.blue, backgroundColor: THC.faint }}
                data-testid="thc-referral-partner-card">
                <div className="text-[12px] font-black uppercase tracking-[0.08em]" style={{ color: THC.muted }}>
                  YOUR LOCAL PRISTINE WATER SYSTEMS PARTNER
                </div>
                <div className="mt-1.5 text-2xl font-extrabold" style={{ color: THC.navy }}
                  data-testid="thc-referral-partner-name">
                  {partner.business_name}
                </div>
                <div className="mt-3 space-y-1.5">
                  {partner.phone && (
                    <a href={`tel:${partner.phone.replace(/\s+/g, "")}`}
                      className="flex items-center gap-2 text-[16px] font-semibold"
                      style={{ color: THC.blue }} data-testid="thc-referral-partner-phone">
                      <Phone className="w-4 h-4" aria-hidden="true" /> {partner.phone}
                    </a>
                  )}
                  {partner.email && (
                    <a href={`mailto:${partner.email}`}
                      className="flex items-center gap-2 text-[16px] font-semibold break-all"
                      style={{ color: THC.blue }} data-testid="thc-referral-partner-email">
                      <Mail className="w-4 h-4 shrink-0" aria-hidden="true" /> {partner.email}
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-7 rounded-[16px] border-2 p-5 md:p-6"
                style={{ borderColor: THC.yellow, backgroundColor: THC.yellowSoft }}
                data-testid="thc-referral-partner-pending">
                <p className="text-base leading-[1.55]" style={{ color: THC.body }}>
                  Your enquiry has been received and is being matched with an appropriate
                  Pristine Water Systems Partner. We&rsquo;ll send you their contact details
                  once confirmed.
                </p>
              </div>
            )}

            {referral?.reference && (
              <p className="mt-6 text-sm" style={{ color: THC.muted }} data-testid="thc-referral-reference">
                Your reference: <strong style={{ color: THC.navy }}>{referral.reference}</strong>
              </p>
            )}
            <p className="mt-7 text-sm">
              <Link to="/" className="font-semibold underline" style={{ color: THC.blue }}
                data-testid="thc-referral-home-link">
                Back to AWARE Water Testing
              </Link>
            </p>
          </>
        )}
      </main>
      <CampaignFooter />
    </div>
  );
}
